/**
 * useFlowChips — Builds context-aware flow chip arrays for the Activity Bar.
 *
 * Returns `{ chips: ChipDef[], exclusive: boolean }` for each active flow state:
 *   - Recording active: stop / pause / select mic
 *   - Recording prep: begin recording / upload instead / quick view / select mic
 *   - Export prep: begin export / cancel
 *   - Upload prep: begin upload / toggle mode / cancel
 *   - Processing complete: view document
 *
 * When `exclusive` is true, TriggerChips renders only flow chips (hides normal chips).
 *
 * Extracted from ActivityBar.js to reduce component size (~150 lines).
 * Also fixes the legacy `flowChips.exclusive` array-property pattern.
 *
 * See: docs/features/infrastructure/app-shell.md#activity-bar
 */
import { useMemo } from "react";
import { Mic, FileDown, Upload, Square, Pause, Play, FileText, Loader, X, FileStack } from "lucide-react";
import { minimumTextLength } from "../globals";

/**
 * @param {Object}   params
 * @param {boolean}  params.isStreaming
 * @param {boolean}  params.showRecordingConfirmation
 * @param {boolean}  params.showExportUI
 * @param {boolean}  params.showUploadActive
 * @param {Object}   params.session
 * @param {Object}   params.processing
 * @param {string}   params.selectedExportType
 * @param {boolean}  params.hasExportData
 * @param {string}   params.exportButtonText
 * @param {Function} params.handleConfirmExport
 * @param {Function} params.handleCancelExport
 * @param {Function} params.handleBeginUpload
 * @param {Function} params.handleCancelUpload
 * @param {string}   params.uploadMode
 * @param {File|null} params.selectedFile
 * @param {string}   params.uploadTextContent
 * @param {string}   params.uploadStatus
 * @param {string}   params.uploadDocId
 * @param {Function} params.navigate
 * @param {string}   params.patientId
 * @param {Function} params.closeActivityBar
 * @param {Function} params.openActivityBar
 * @param {Function} params.setMicMenuAnchor
 * @param {Function} params.setUploadMode
 * @param {Function} params.setSelectedFile
 * @param {Function} params.setUploadTextContent
 * @returns {{ chips: Array, exclusive: boolean }}
 */
export function useFlowChips({
    isStreaming,
    showRecordingConfirmation,
    showExportUI,
    showUploadActive,
    showSummaryUI,
    showContinue,
    session,
    processing,
    selectedExportType,
    hasExportData,
    exportButtonText,
    handleConfirmExport,
    handleCancelExport,
    handleBeginUpload,
    handleCancelUpload,
    selectedSummaryTypeId,
    summaryButtonText,
    summaryDocumentTypes,
    handleConfirmSummary,
    handleCancelSummary,
    uploadMode,
    selectedFile,
    uploadTextContent,
    uploadStatus,
    uploadDocId,
    navigate,
    patientId,
    closeActivityBar,
    openActivityBar,
    setMicMenuAnchor,
    setUploadMode,
    setSelectedFile,
    setUploadTextContent,
}) {
    return useMemo(() => {
        // Recording starting — bridge between confirmStart and isStreaming=true
        if (session?.isStarting) {
            return {
                chips: [
                    {
                        id: "flow-getting-ready",
                        label: "Getting ready…",
                        icon: Loader,
                        disabled: true,
                    },
                ],
                exclusive: true,
            };
        }

        // Recording active — cancel/pause/stop buttons (exclusive)
        if (isStreaming) {
            const paused = session?.isPaused;
            const isStopping = session?.currentStatus === "Stopping recording...";
            return {
                chips: [
                    {
                        id: "flow-select-mic",
                        label: session?.micJustChanged ? "Mic Changed" : "Select Mic",
                        icon: Mic,
                        micChanged: session?.micJustChanged,
                        onClick: (e) => {
                            const target = e?.currentTarget || e?.target;
                            setMicMenuAnchor(target || null);
                        },
                    },
                    // Cancellation always opens a destructive-action warning dialog.
                    {
                        id: "flow-cancel-recording",
                        label: "Cancel",
                        icon: X,
                        danger: true,
                        disabled: isStopping,
                        onClick: () => session?.requestCancel?.(),
                    },
                    {
                        id: "flow-pause",
                        label: paused ? "Resume" : "Pause",
                        icon: paused ? Play : Pause,
                        disabled: isStopping,
                        onClick: () => {
                            paused ? session?.resumeRecording?.() : session?.pauseRecording?.();
                        },
                    },
                    {
                        id: "flow-stop",
                        label: isStopping ? "Stopping..." : "Stop & Process",
                        icon: Square,
                        primary: true,
                        disabled: isStopping,
                        onClick: () => {
                            session?.stop?.();
                        },
                    },
                ],
                exclusive: true,
            };
        }
        // Recording prep — mic is ready, show "Patient Confirms" to start recording.
        // Exclusive: hides all other action chips for a clear, focused prompt.
        if (showRecordingConfirmation) {
            return {
                chips: [
                    {
                        id: "flow-cancel-recording",
                        label: "Cancel",
                        onClick: () => session?.cancelConfirmation?.(),
                    },
                    {
                        id: "flow-select-mic",
                        label: session?.micJustChanged ? "Mic Changed" : "Select Mic",
                        icon: Mic,
                        micChanged: session?.micJustChanged,
                        onClick: (e) => {
                            const target = e?.currentTarget || e?.target;
                            setMicMenuAnchor(target || null);
                        },
                    },
                    {
                        id: "flow-patient-confirms",
                        label: "Patient confirms?",
                        icon: Mic,
                        dangerFilled: true,
                        onClick: () => session?.confirmStart?.(),
                    },
                ],
                exclusive: true,
                confirmationActive: true,
            };
        }
        // Export prep — begin export + cancel (exclusive)
        if (showExportUI) {
            return {
                chips: [
                    {
                        id: "flow-cancel-export",
                        label: "Cancel",
                        onClick: handleCancelExport,
                    },
                    {
                        id: "flow-begin-export",
                        label: exportButtonText,
                        icon: FileDown,
                        primary: true,
                        disabled: !selectedExportType || !hasExportData,
                        disabledReason: !hasExportData ? "Not enough data" : "Select an export type",
                        onClick: handleConfirmExport,
                    },
                ],
                exclusive: true,
            };
        }
        // Summary prep — generate + cancel (exclusive)
        if (showSummaryUI) {
            const hasTypes = (summaryDocumentTypes || []).length > 0;
            return {
                chips: [
                    {
                        id: "flow-cancel-summary",
                        label: "Cancel",
                        onClick: handleCancelSummary,
                    },
                    {
                        id: "flow-generate-summary",
                        label: summaryButtonText || "Generate",
                        icon: FileStack,
                        primary: true,
                        disabled: !hasTypes || !selectedSummaryTypeId,
                        disabledReason: !hasTypes ? "No summary types available" : "Select a summary type",
                        onClick: handleConfirmSummary,
                    },
                ],
                exclusive: true,
            };
        }
        // Upload prep — file/text select + begin upload (exclusive)
        if (showUploadActive && uploadStatus === "idle") {
            const isFileMode = uploadMode === "file";
            const isValid = isFileMode ? !!selectedFile : uploadTextContent.length >= minimumTextLength;
            return {
                chips: [
                    {
                        id: "flow-cancel-upload",
                        label: "Cancel",
                        onClick: handleCancelUpload,
                    },
                    {
                        id: "flow-toggle-upload-mode",
                        label: isFileMode ? "Text Paste" : "File Upload",
                        icon: isFileMode ? FileText : Upload,
                        onClick: () => {
                            setUploadMode(isFileMode ? "text" : "file");
                            setSelectedFile(null);
                            setUploadTextContent("");
                        },
                    },
                    {
                        id: "flow-begin-upload",
                        label: isFileMode ? "Begin Upload" : "Begin Processing",
                        icon: Upload,
                        primary: true,
                        disabled: !isValid,
                        disabledReason: isFileMode ? "Select a file first" : `Need at least ${minimumTextLength} characters`,
                        onClick: handleBeginUpload,
                    },
                ],
                exclusive: true,
            };
        }
        // No more flow chips — return default
        return { chips: [], exclusive: false };
    }, [isStreaming, showRecordingConfirmation, showExportUI, showUploadActive, showSummaryUI, showContinue,
        session, processing, selectedExportType, hasExportData, exportButtonText,
        handleConfirmExport, handleCancelExport, handleBeginUpload, handleCancelUpload,
        selectedSummaryTypeId, summaryButtonText, summaryDocumentTypes,
        handleConfirmSummary, handleCancelSummary,
        uploadMode, selectedFile, uploadTextContent, uploadStatus, uploadDocId,
        navigate, patientId, closeActivityBar, openActivityBar,
        setMicMenuAnchor, setUploadMode, setSelectedFile, setUploadTextContent]);
}
